<template>
  <div class="transcript fullHeight">
    <div class="transcript-layout">
      <div>
        <div class="intro">
          <h1 :class="webView ? 'web-view-intro' : 'normal-intro'">{{ t('transcript_intro_' + mode + '_title') }}</h1>
          <span v-if="state === 'form'">
            <p>{{ t('transcript_messages_intro') }}</p>
          </span>
        </div>
        <div class="main-container">
          <div v-if="state === 'dataTransfer'" class="data-transfer">
            <h1>{{ t('dataTransfer_title') }}</h1>
            <p>{{ t('dataTransfer_intro') }}</p>
          </div>
          <SendProtocolForm
            v-else
            :is-sent="isSent"
            @onEmailChange="onEmailChange"
          />
        </div>
      </div>
      <div class="buttons-container" :class="$mqAliases.isMobile ? 'mobile-buttons' : (webView ? 'web-view-buttons' : '')">
        <ChatButton
          v-if="!webView && state !== 'dataTransfer'"
          class="sanitas-button"
          :disabled="emailContent && emailContent.length === 0 || !valid || isSent"
          :types="['primary']"
          @click="onSubmit"
        >
          {{ t('transcript_buttons_continue') }}
        </ChatButton>
        <ChatButton
          v-if="webView && state !== 'dataTransfer'"
          :types="['primary']"
          class="sanitas-button"
          :disabled="emailContent && emailContent.length === 0 || !valid || isSent"
          @click="onSubmit"
        >{{ t('transcript_title') }}</ChatButton>
        <ChatButton v-if="state === 'form'"
          class="sanitas-button"
          :types="['secondary']"
          @click="isCustomerFlow ? openSOS() : openRating()"
        >{{ t('transcript_buttons_no') }}</ChatButton>
        <ChatButton v-if="state === 'dataTransfer'"
          class="sanitas-button"
          :types="['primary']"
          @click="dataTransferYes()"
        >{{ t('dataTransfer_buttons_yes') }}</ChatButton>
        <ChatButton v-if="state === 'dataTransfer'"
          class="sanitas-button"
          :types="['secondary']"
          @click="dataTransferNo()"
        >{{ t('dataTransfer_buttons_no') }}</ChatButton>
      </div>
    </div>
  </div>
</template>

<script>
import SendProtocolForm from './SendProtocolForm'
import { useI36n } from '@jota-one/i36n/src/index.vue2'
import { mapActions, mapGetters } from 'vuex'
import ChatButton from './ChatButton'

export default {
  name: 'transcript',
  components: {
    ChatButton,
    SendProtocolForm
  },
  props: {
    mode: {
      type: String,
      default: 'agent'
    }
  },
  data () {
    return {
      emailContent: '',
      isSent: false
    }
  },
  setup () {
    const { $label: t } = useI36n()

    return { t }
  },

  computed: {
    ...mapGetters('conversation', {
      agent: 'getLatestAgent',
      isUserAuthenticated: 'isUserAuthenticated',
      user: 'getUser'
    }),
    ...mapGetters('uistate', ['isCustomerFlow', 'webView']),
    valid () {
      const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      return pattern.test(this.emailContent) || false
    },
    email: {
      get() {
        return this.$store.state.uistate.transcriptEmail
      },
      set(value) {
        this.$store.commit('uistate/SET_TRANSCRIPT_EMAIL', value)
      }
    },
    state: {
      get () {
        return this.$store.state.uistate.transcriptState
      },
      set (value) {
        this.$store.commit('uistate/SET_TRANSCRIPT_STATE', value)
      }
    }
  },
  created () {
    if (this.isUserAuthenticated && !this.isCustomerFlow) {
      this.state = 'form'
    }
    this.emailContent = this.user.email
  },
  methods: {
    ...mapActions('conversation', ['resumeConversation', 'sendDataTransferFlag']),
    onEmailChange(content) {
      this.emailContent = content
    },
    async onSubmit() {
      if (!this.valid) {
        return
      }

      this.isSent = true

      try {
        await this.$store.dispatch('conversation/sendTranscript',{ email: this.emailContent })
        this.email = this.emailContent
        this.state = 'sent'
        this.isSent = false
        if (this.isCustomerFlow) {
          return this.openSOS()
        } else {
          return this.openRating()
        }
      } catch (err) {
        if (err) {
          self.isSent = true
        }
      }
    },
    async openSOS() {
      this.$store.commit('uistate/SET_TRANSCRIPT_STATE', 'dataTransfer')
      await this.$store.dispatch('conversation/archiveConversation')
      return this.$store.dispatch('uistate/setConversationState', 'CONVERSATION_ENDED', null, { root: true })
    },
    openRating () {
      if (!this.isUserAuthenticated) {
        this.state = 'dataTransfer'
      }
      return this.$store.dispatch('conversation/openRating', this.mode)
    },
    dataTransferYes () {
      this.sendDataTransferFlag({
        flag: true
      }).then(() => {
        if (this.isCustomerFlow) {
          this.$store.dispatch('uistate/setConversationState', 'CONVERSATION_RATING')
        } else {
          this.state = 'form'
        }
      }
      )
    },
    async dataTransferNo () {
      await this.sendDataTransferFlag({ flag: false })
      
      if (this.isCustomerFlow) {
        this.$store.dispatch('uistate/setConversationState', 'CONVERSATION_RATING')
      } else {
        this.state = 'form'
      }
    },
    close () {
      if (!this.isUserAuthenticated) {
        this.state = 'dataTransfer'
      }
      this.resumeConversation()
    }
  }
}
</script>

<style scoped lang="postcss">
.transcript {
  overflow: auto;
  height: 100%;
  box-shadow: 0 6px 6px -3px rgba(0,0,0,0.2), 0 10px 14px 1px rgba(0,0,0,0.14), 0 4px 18px 3px rgba(0,0,0,0.12);
  padding: 15px;
  background-color: white;
  p, h1 {
    font-size: 15px;
    color: #191919;
  }
  & >>> button {
    font-size: 15px;
    padding-top: 10px;
    padding-bottom: 10px;
  }
  &.fullHeight {
    height: 100%;
  }
  &-layout {
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    height: 100%;
  }
  .main-container {
    margin: 0;
    width: 100%;
    .data-transfer {
      margin: 15px 25px 0 25px;
    }
    p {
      margin-bottom: 16px;
    }
  }
  h1 {
    font-size: 15px;
    font-family: 'Open Sans', sans-serif;
    color: var(--tw-c-medium-gray);
    font-weight: bold;
    margin-bottom: 10px;
  }
  p {
    font-size: 13px;
  }
  .already-sent {
    display: flex;
  }
  .form-container {
    background-color: white;
    border-radius: 4px;
    padding: 0;
    margin: 0;

    .already-sent p {
      text-align: center;
      & >>> strong {
        font-weight: inherit;
      }
    }
  }

  .buttons-container {
    align-self: end;
    padding: 0 15px;
    width: 100%;

    .sanitas-button {
      border-radius: 25px;
      height: 50px;
      width: 100%;
    }
  }

  .mobile-buttons {
    width: 100%;
    margin-top: auto;
  }

  .sanitas-button {
    height: 50px;
    width: 100%;
    border-radius: 25px;
    &.close {
      position: absolute;
      right: 12px;
      top: 5px;
      width: 32px;
      height: 32px;
    }
  }
  .stacked {
    position: relative;
    span {
      position: absolute;
    }
  }
  .big-icon {
    color: var(--tw-c-sanitas-green);
    fill: var(--tw-c-sanitas-green);
    height: 60px;
    width: 100%;
    margin-bottom: 25px;
    span {
      left: calc(50% - 30px)
    }
  }
  .intro {
    width: 100%;
    padding-left: 25px;
    padding-right: 25px;
    text-align: center;
    h1 {
      color: #191919;
      font-size: 25px;
    }
    h1.normal-intro {
      margin-top: 65px;
    }
    h1.web-view-intro {
      margin-top: 10px;
    }
    p {
      margin-top: 20px;
      text-align: left;
      font-size: 15px;
    }
  }
}
</style>
